import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

type View = 'login' | 'register' | 'verify';

const ParentLogin: React.FC = () => {
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupsEnabled, setSignupsEnabled] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/auth/config')
      .then((res) => setSignupsEnabled(!!res.data?.signupsEnabled))
      .catch(() => setSignupsEnabled(false));
  }, []);

  const finishLogin = (data: any) => {
    setAuth(data.token, 'PARENT', data.userId, data.name);
    navigate('/parent/home');
  };

  const msg = (err: any, fallback: string) =>
    err?.response?.data?.message || fallback;

  const handleGoogleLogin = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const res = await api.post('/api/auth/parent/google', { idToken });
      finishLogin(res.data);
    } catch (err: any) {
      if (
        err?.code === 'auth/popup-closed-by-user' ||
        err?.code === 'auth/cancelled-popup-request'
      ) {
        setError('');
      } else {
        console.error('Google login error:', err);
        setError(msg(err, 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await api.post('/api/auth/parent/login', { email, password });
      finishLogin(res.data);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        // Email not verified yet — send a fresh code and switch to verify.
        setView('verify');
        setInfo('Please verify your email. We just sent you a code.');
        try { await api.post('/api/auth/parent/resend', { email }); } catch { /* throttled is fine */ }
      } else {
        setError(msg(err, 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!consent) {
      setError('Please agree to the Privacy Policy and Terms to continue.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/parent/register', { email, password, name, consent });
      setView('verify');
      setInfo('We sent a 6-digit code to your email. Enter it below.');
    } catch (err: any) {
      setError(msg(err, 'Could not register. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await api.post('/api/auth/parent/verify', { email, code });
      finishLogin(res.data);
    } catch (err: any) {
      setError(msg(err, 'Verification failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    try {
      await api.post('/api/auth/parent/resend', { email });
      setInfo('A new code has been sent.');
    } catch (err: any) {
      setError(msg(err, 'Could not resend the code.'));
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-300';

  const switchView = (v: View) => {
    setView(v);
    setError('');
    setInfo('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-purple-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">👨‍👩‍👧</div>
          <h1 className="text-2xl font-bold text-purple-600">Parent Login</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your kids' learning journey</p>
        </div>

        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
        {info && <p className="text-green-600 text-sm text-center mb-4">{info}</p>}

        {/* Google sign-in (always available) */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-3 hover:bg-gray-50 transition disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="font-medium text-gray-700">
            {loading ? 'Please wait...' : 'Sign in with Google'}
          </span>
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-gray-200 flex-1" />
          <span className="text-xs text-gray-400">or use email</span>
          <div className="h-px bg-gray-200 flex-1" />
        </div>

        {/* Email + password login */}
        {view === 'login' && (
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <input type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputClass} />
            <input type="password" required placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} className={inputClass} />
            <button type="submit" disabled={loading}
              className="w-full bg-purple-600 text-white font-semibold rounded-lg px-4 py-3 hover:bg-purple-700 transition disabled:opacity-50">
              {loading ? 'Signing in...' : 'Log in'}
            </button>
            {signupsEnabled ? (
              <p className="text-center text-sm text-gray-500">
                New here?{' '}
                <span className="text-purple-600 cursor-pointer hover:underline"
                  onClick={() => switchView('register')}>Create an account</span>
              </p>
            ) : (
              <p className="text-center text-xs text-gray-400">New accounts are added by the administrator.</p>
            )}
          </form>
        )}

        {/* Register */}
        {view === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3">
            <input type="text" placeholder="Name (optional)" value={name}
              onChange={(e) => setName(e.target.value)} className={inputClass} />
            <input type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputClass} />
            <input type="password" required placeholder="Password (min 8 chars)" value={password}
              onChange={(e) => setPassword(e.target.value)} className={inputClass} />
            <input type="password" required placeholder="Confirm password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className={inputClass} />
            <label className="flex items-start gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5" />
              <span>I'm the parent/guardian and agree to the terms of use.</span>
            </label>
            <button type="submit" disabled={loading}
              className="w-full bg-purple-600 text-white font-semibold rounded-lg px-4 py-3 hover:bg-purple-700 transition disabled:opacity-50">
              {loading ? 'Creating...' : 'Create account'}
            </button>
            <p className="text-center text-sm text-gray-500">
              Already have an account?{' '}
              <span className="text-purple-600 cursor-pointer hover:underline"
                onClick={() => switchView('login')}>Log in</span>
            </p>
          </form>
        )}

        {/* Verify OTP */}
        {view === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-3">
            <p className="text-sm text-gray-600 text-center">
              Enter the 6-digit code sent to<br /><span className="font-semibold">{email}</span>
            </p>
            <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
              placeholder="------" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`${inputClass} text-center text-2xl tracking-[0.5em]`} />
            <button type="submit" disabled={loading}
              className="w-full bg-purple-600 text-white font-semibold rounded-lg px-4 py-3 hover:bg-purple-700 transition disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify & continue'}
            </button>
            <p className="text-center text-sm text-gray-500">
              Didn't get it?{' '}
              <span className="text-purple-600 cursor-pointer hover:underline" onClick={handleResend}>Resend code</span>
              {' · '}
              <span className="text-purple-600 cursor-pointer hover:underline" onClick={() => switchView('login')}>Back to login</span>
            </p>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-5">
          Are you a kid?{' '}
          <span className="text-purple-600 cursor-pointer hover:underline" onClick={() => navigate('/')}>
            Login here
          </span>
        </p>
      </div>
    </div>
  );
};

export default ParentLogin;
